import { useEffect, useState } from 'react';
import { getDataFromContentful } from '../utils/getDataFromContentful';
import { usePreviewMode } from './usePreviewMode';

// Shared fetch/poll plumbing every Contentful-backed section needs: fetch on
// mount, poll every 2s only in ?preview=true (for the standalone preview
// URL — useContentfulLiveUpdates handles the in-editor iframe case
// separately), and never update state after unmount. Callers get the raw
// `data` object back and destructure whatever collection they need from it —
// `useContentfulLiveUpdates` only accepts an Entity[] shape, so callers apply
// it themselves to the specific array they extract, not to this wrapper.
export function useContentfulData<T = any>(buildQuery: (preview: boolean) => string) {
    const isPreview = usePreviewMode();
    const [data, setRawData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        let initialDone = false;

        async function fetchData() {
            try {
                const response = await getDataFromContentful({ query: buildQuery(isPreview), preview: isPreview });
                if (cancelled) return;
                if (response.data) setRawData(response.data);
            } catch (error) {
                console.error('Error fetching Contentful data:', error);
            } finally {
                if (!cancelled && !initialDone) {
                    initialDone = true;
                    setIsLoading(false);
                }
            }
        }

        fetchData();

        const interval = isPreview ? setInterval(fetchData, 2000) : undefined;

        return () => {
            cancelled = true;
            if (interval) clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreview]);

    return { data, isLoading };
}
